/**
 * imagepreprocess — клиентское пресжатие изображений перед загрузкой на сервер
 * (Фаза 12, эпик A2).
 *
 * Проблема: телефонные фото (JPEG 3–8 МиБ) отбрасываются сервером с 413 ДО
 * сжатия (http.MaxBytesReader на 2 МиБ сырого тела). Конвейер ниже ужимает
 * картинку в браузере через Canvas до ~0.1–0.2 МБ и кодирует в WebP — серверный
 * конвейер (imageutil, A1) остаётся авторитетным по формату/лимиту, здесь только
 * экономия трафика пользователя и обход сырого лимита.
 *
 * Контракт: `preprocessImage(file)` НИКОГДА не бросает и не возвращает null —
 * любая ошибка (нет canvas в окружении, сбой декодирования/кодирования) ⇒
 * возвращается исходный File: пресжатие строго best-effort, загрузка важнее.
 *
 * Конвейер:
 *   1. GIF — пропускается как есть (сервер берёт первый кадр; анимацию клиентское
 *      пресжатие всё равно убило бы).
 *   2. Декодирование: createImageBitmap(file, {imageOrientation:'from-image'})
 *      (EXIF-ориентация применяется браузером), fallback — <img> + decode()
 *      (CSS image-orientation: from-image применяется браузерами по умолчанию).
 *   3. Downscale до ~1600 px по большей стороне: итеративно ÷2, пока сторона
 *      > 2× цели (несколько шагов дают лучшее качество, чем один большой прыжок),
 *      затем финальный проход; imageSmoothingQuality='high'.
 *   4. canvas.toBlob('image/webp', 0.85). Safari не кодирует WebP из canvas —
 *      feature-detect по blob.type; fallback JPEG 0.85 с заливкой нейтрального
 *      белого фона (JPEG без альфа-канала).
 *   5. Если результат не меньше исходника — возвращается исходный File.
 *
 * DI (PreprocessDeps): jsdom не реализует canvas 2d-контекст и энкодинг, поэтому
 * точки интеграции с платформой вынесены в опциональные зависимости — тесты
 * подменяют createBitmap/makeCanvas/toBlob и проверяют оркестрацию конвейера.
 */

/** Целевая большая сторона после пресжатия (px). */
const TARGET_MAX_DIM = 1600;

/** Качество кодирования WebP/JPEG (0.85 — визуально прозрачно при ~0.2 МБ на 1600 px). */
const ENCODE_QUALITY = 0.85;

/**
 * DrawSurface — канвас с готовым 2d-контекстом. Абстракция над
 * document.createElement('canvas') + getContext('2d') для DI в тестах
 * (jsdom getContext('2d') возвращает null).
 */
export interface DrawSurface {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

/** PreprocessDeps — опциональные платформенные зависимости (DI для тестов). */
export interface PreprocessDeps {
  /** Декодер файла в растровый источник (default: createImageBitmap → <img>). */
  createBitmap?: (file: File) => Promise<ImageBitmap | HTMLImageElement>;
  /** Фабрика canvas-поверхностей (default: document.createElement). */
  makeCanvas?: (width: number, height: number) => DrawSurface;
  /** Кодировщик canvas → Blob (default: canvas.toBlob через Promise). */
  toBlob?: (canvas: HTMLCanvasElement, type: string, quality?: number) => Promise<Blob | null>;
}

/**
 * preprocessImage сжимает изображение для загрузки (см. доку модуля). GIF и все
 * неужимаемые/ошибочные случаи возвращают исходный File (never-throw контракт).
 */
export async function preprocessImage(file: File, deps: PreprocessDeps = {}): Promise<File> {
  if (isGif(file)) {
    return file;
  }
  try {
    const source = await (deps.createBitmap ?? decodeFile)(file);
    const w = sourceWidth(source);
    const h = sourceHeight(source);
    if (w <= 0 || h <= 0) {
      return file;
    }
    const mk = deps.makeCanvas ?? defaultMakeCanvas;

    // Итеративный downscale ÷2, пока большая сторона > 2× цели, затем финальный
    // проход до цели (resize до ~1600 px по большей стороне, пропорции сохранены).
    let cur: CanvasImageSource = source;
    let cw = w;
    let ch = h;
    let surface: DrawSurface | undefined;
    while (Math.max(cw, ch) > TARGET_MAX_DIM * 2) {
      const nw = Math.max(1, Math.round(cw / 2));
      const nh = Math.max(1, Math.round(ch / 2));
      surface = drawSurface(mk, cur, nw, nh);
      cur = surface.canvas;
      cw = nw;
      ch = nh;
    }
    if (Math.max(cw, ch) > TARGET_MAX_DIM) {
      const k = TARGET_MAX_DIM / Math.max(cw, ch);
      surface = drawSurface(
        mk,
        cur,
        Math.max(1, Math.round(cw * k)),
        Math.max(1, Math.round(ch * k)),
      );
    } else if (!surface) {
      // Картика меньше цели — canvas 1:1 (toBlob требует canvas-источник).
      surface = drawSurface(mk, source, cw, ch);
    }

    const toBlob = deps.toBlob ?? canvasToBlob;
    const webp = await toBlob(surface.canvas, 'image/webp', ENCODE_QUALITY);
    if (webp?.type === 'image/webp') {
      return pickSmaller(webp, file);
    }

    // Safari-детект: blob.type не webp (или null) — canvas не умеет WebP →
    // JPEG-fallback с заливкой нейтрального белого фона (JPEG без альфы).
    const opaque = drawSurface(mk, surface.canvas, surface.canvas.width, surface.canvas.height, {
      background: '#ffffff',
    });
    const jpeg = await toBlob(opaque.canvas, 'image/jpeg', ENCODE_QUALITY);
    return jpeg ? pickSmaller(jpeg, file) : file;
  } catch {
    return file;
  }
}

/** isGif — анимации сервер берёт первым кадром; пресжатие не вмешивается. */
function isGif(file: File): boolean {
  return file.type === 'image/gif' || /\.gif$/i.test(file.name);
}

/** decodeFile — createImageBitmap (EXIF через imageOrientation) с fallback <img>+decode(). */
async function decodeFile(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // fallthrough: <img>+decode() ниже (старые браузеры / битый EXIF).
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    // После decode() битмап уже в памяти элемента; URL больше не нужен.
    URL.revokeObjectURL(url);
  }
}

/** sourceWidth/sourceHeight — размеры источника (bitmap или <img>). */
function sourceWidth(src: ImageBitmap | HTMLImageElement): number {
  return src instanceof HTMLImageElement ? src.naturalWidth : src.width;
}

function sourceHeight(src: ImageBitmap | HTMLImageElement): number {
  return src instanceof HTMLImageElement ? src.naturalHeight : src.height;
}

/**
 * drawSurface — отрисовка source на новую canvas-поверхность w×h c
 * imageSmoothingQuality='high'. options.background — заливка под источником
 * (для JPEG-fallback: нейтральный белый фон вместо чёрной прозрачности).
 */
function drawSurface(
  mk: NonNullable<PreprocessDeps['makeCanvas']>,
  source: CanvasImageSource,
  width: number,
  height: number,
  options?: { background: string },
): DrawSurface {
  const { canvas, ctx } = mk(width, height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (options) {
    ctx.fillStyle = options.background;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(source, 0, 0, width, height);
  return { canvas, ctx };
}

/** defaultMakeCanvas — платформенная фабрика canvas (jsdom: getContext→null ⇒ throw ⇒ safe path). */
function defaultMakeCanvas(width: number, height: number): DrawSurface {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('imagepreprocess: canvas 2d context unavailable');
  }
  return { canvas, ctx };
}

/** canvasToBlob — Promise-обёртка над canvas.toBlob (null = ошибка кодирования). */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

/** pickSmaller — результат пресжатия, если он строго меньше исходника; иначе исходник. */
function pickSmaller(result: Blob, original: File): File {
  if (result.size >= original.size) {
    return original;
  }
  const ext = result.type === 'image/webp' ? 'webp' : 'jpg';
  const base = original.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([result], `${base}.${ext}`, {
    type: result.type,
    lastModified: original.lastModified,
  });
}
